import axios, { type AxiosError } from "axios";
import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import type {
  PaginatedReactionResponse,
  ReactionType,
} from "@/lib/humhub-api/types";

const isAxiosError = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error);

const handleReactionApiError = (error: unknown, context: string): never => {
  if (isAxiosError(error)) {
    if (!error.response) {
      throw new Error("Please check your connection");
    }

    console.error(`${context}:`, error.response.data);
    throw error;
  }

  console.error(`Unexpected ${context}:`, error);
  throw new Error("Unexpected error occurred");
};

const withReactionApiHandling = async <T>(
  context: string,
  request: () => Promise<T>,
): Promise<T> => {
  try {
    return await request();
  } catch (error: unknown) {
    return handleReactionApiError(error, context);
  }
};

export const reactionsApi = {
  getReactions: async (
    contentId: number | string,
    page = 1,
    perPage = 100,
    emotion?: ReactionType,
  ): Promise<PaginatedReactionResponse> => {
    return withReactionApiHandling("Reaction API Error", async () => {
      const response = await api.get<PaginatedReactionResponse>(
        `${API_URLS.REACTION}/content/${contentId}`,
        {
          params: {
            page,
            "per-page": perPage,
            ...(emotion ? { emotion } : {}),
          },
        },
      );

      return response.data;
    });
  },

  findByObject: async (
    objectModel: string,
    objectId: number | string,
    page = 1,
    perPage = 100,
    emotion?: ReactionType,
  ): Promise<PaginatedReactionResponse> => {
    return withReactionApiHandling(
      "Reaction find-by-object API Error",
      async () => {
        const response = await api.get<PaginatedReactionResponse>(
          `${API_URLS.REACTION}/find-by-object`,
          {
            params: {
              objectModel,
              objectId,
              page,
              "per-page": perPage,
              ...(emotion ? { emotion } : {}),
            },
          },
        );

        return response.data;
      },
    );
  },
};
